import React, { useState, useEffect } from 'react';
import { Webhook, Plus, Trash2, Play, Eye, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { WebhookService, IntegrationActivityLogger } from '../services/partnerIntegrationService';
import { PartnerWebhook, WebhookDeliveryLog, WebhookEventType } from '../types';

interface WebhooksConfigProps {
  partnerId: string;
  userId?: string;
  userName?: string;
}

export const WebhooksConfig: React.FC<WebhooksConfigProps> = ({
  partnerId,
  userId,
  userName
}) => {
  const [webhooks, setWebhooks] = useState<PartnerWebhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<string | null>(null);
  const [deliveryLogs, setDeliveryLogs] = useState<WebhookDeliveryLog[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  // New webhook form
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookName, setNewWebhookName] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState<WebhookEventType[]>([]);

  const availableEvents: { value: WebhookEventType; label: string }[] = [
    { value: 'campaign_status_updates', label: 'Campaign Status Updates' },
    { value: 'campaign_created', label: 'Campaign Created' },
    { value: 'campaign_updated', label: 'Campaign Updated' },
    { value: 'promo_code_requested', label: 'Promo Code Requested' },
    { value: 'advertisement_created', label: 'Advertisement Created' },
    { value: 'advertisement_approved', label: 'Advertisement Approved' },
    { value: 'advertisement_rejected', label: 'Advertisement Rejected' },
  ];

  useEffect(() => {
    loadWebhooks();
  }, [partnerId]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const hooks = await WebhookService.getWebhooks(partnerId);
      setWebhooks(hooks);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      if (!newWebhookUrl) {
        setMessage({ type: 'error', text: 'Please enter a webhook URL' });
        return;
      }

      // Validate URL
      try {
        new URL(newWebhookUrl);
      } catch {
        setMessage({ type: 'error', text: 'Please enter a valid URL' });
        return;
      }

      const newWebhook = await WebhookService.createWebhook(partnerId, {
        webhook_url: newWebhookUrl,
        webhook_name: newWebhookName || 'Default Webhook',
        event_subscriptions: newWebhookEvents
      });

      if (newWebhook) {
        setWebhooks([newWebhook, ...webhooks]);
        setShowCreateModal(false);
        setNewWebhookUrl('');
        setNewWebhookName('');
        setNewWebhookEvents([]);
        setMessage({ type: 'success', text: 'Webhook created successfully!' });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'webhook_access',
          'created',
          userId,
          userName,
          { webhook_name: newWebhook.webhook_name, webhook_url: newWebhook.webhook_url }
        );

        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to create webhook' });
    }
  };

  const handleDeleteWebhook = async (webhookId: string, webhookName?: string) => {
    if (!confirm(`Are you sure you want to delete the webhook "${webhookName || 'Unnamed'}"?`)) {
      return;
    }

    try {
      const success = await WebhookService.deleteWebhook(webhookId);
      if (success) {
        setWebhooks(webhooks.filter(w => w.id !== webhookId));
        setMessage({ type: 'success', text: 'Webhook deleted successfully' });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'webhook_access',
          'deleted',
          userId,
          userName,
          { webhook_name: webhookName }
        );

        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete webhook' });
    }
  };

  const handleToggleEnabled = async (webhook: PartnerWebhook) => {
    try {
      const updated = await WebhookService.updateWebhook(webhook.id, { enabled: !webhook.enabled });
      if (updated) {
        setWebhooks(webhooks.map(w => w.id === webhook.id ? updated : w));
        setMessage({ type: 'success', text: `Webhook ${updated.enabled ? 'enabled' : 'disabled'}` });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'webhook_access',
          updated.enabled ? 'enabled' : 'disabled',
          userId,
          userName,
          { webhook_name: webhook.webhook_name }
        );

        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update webhook' });
    }
  };

  const handleTestWebhook = async (webhookId: string) => {
    try {
      setTesting(webhookId);
      const result = await WebhookService.testWebhook(webhookId);
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
      } else {
        setMessage({ type: 'error', text: result.message });
      }

      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to test webhook' });
    } finally {
      setTesting(null);
    }
  };

  const handleViewLogs = async (webhookId: string) => {
    try {
      const logs = await WebhookService.getDeliveryLogs(webhookId, 20);
      setDeliveryLogs(logs);
      setSelectedWebhook(webhookId);
    } catch (error) {
      console.error('Failed to load delivery logs:', error);
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading webhooks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Webhook className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h4 className="text-lg font-medium text-gray-900">Webhooks</h4>
            <p className="text-sm text-gray-600">Receive real-time event notifications</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Webhook
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Webhook className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Webhooks</h3>
          <p className="mt-1 text-sm text-gray-500">Add your first webhook endpoint to receive events</p>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h5 className="text-sm font-medium text-gray-900">{webhook.webhook_name}</h5>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      webhook.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {webhook.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 font-mono">{webhook.webhook_url}</p>
                  {webhook.last_triggered_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last triggered: {new Date(webhook.last_triggered_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleTestWebhook(webhook.id)}
                    disabled={testing === webhook.id}
                    className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-50"
                    title="Test webhook"
                  >
                    {testing === webhook.id ? (
                      <Clock className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleViewLogs(webhook.id)}
                    className="p-1 text-gray-500 hover:text-blue-600"
                    title="View logs"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(webhook)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title={webhook.enabled ? 'Disable' : 'Enable'}
                  >
                    <Webhook className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteWebhook(webhook.id, webhook.webhook_name)}
                    className="p-1 text-gray-500 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Event Subscriptions */}
              <div className="text-xs text-gray-600">
                <span className="font-medium">Subscribed events:</span>{' '}
                {webhook.event_subscriptions && webhook.event_subscriptions.length > 0
                  ? webhook.event_subscriptions.map(event => 
                      availableEvents.find(e => e.value === event)?.label || event
                    ).join(', ')
                  : 'None'}
              </div>

              {/* Health Status */}
              {webhook.failure_count > 0 && (
                <div className="mt-2 text-xs text-red-600 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {webhook.failure_count} recent failures
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Add New Webhook</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL *
                </label>
                <input
                  type="url"
                  value={newWebhookUrl}
                  onChange={(e) => setNewWebhookUrl(e.target.value)}
                  placeholder="https://your-domain.com/webhooks/clickandclaim"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook Name
                </label>
                <input
                  type="text"
                  value={newWebhookName}
                  onChange={(e) => setNewWebhookName(e.target.value)}
                  placeholder="e.g., Production Webhook"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Subscriptions
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableEvents.map(({ value, label }) => (
                    <label key={value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newWebhookEvents.includes(value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewWebhookEvents([...newWebhookEvents, value]);
                          } else {
                            setNewWebhookEvents(newWebhookEvents.filter(ev => ev !== value));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWebhook}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Webhook
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Logs Modal */}
      {selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Delivery Logs</h3>
              <button
                onClick={() => setSelectedWebhook(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            {deliveryLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No delivery logs yet</p>
            ) : (
              <div className="space-y-3">
                {deliveryLogs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{log.event_type}</span>
                      <span className={`px-2 py-1 text-xs rounded ${
                        log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>Status: {log.http_status || 'N/A'}</p>
                      <p>Time: {new Date(log.created_at).toLocaleString()}</p>
                      {log.error_message && <p className="text-red-600">Error: {log.error_message}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
